import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import EventRounded from "@mui/icons-material/EventRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import { Link as RouterLink } from "react-router-dom";
import { ApiError } from "@bw/shared/api";
import type {
  CommunityGameNightCreate,
  CommunityGameNightReminderResponse,
  CommunityGameNightResponse,
} from "@bw/shared/types/generated";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { useCommunityGameNights } from "../hooks/useCommunityGameNights";
import { CommunityCoordinationTools } from "./CommunityCoordinationTools";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";


function dateTimeLabel(value: string, timezone: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString(undefined, {
        timeZone: timezone,
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Time unavailable";
}

/** "Fri, Nov 6, 7:00 – 9:00 PM" — the end only when the list has room for it. */
function whenLabel(event: CommunityGameNightResponse, withEnd: boolean): string {
  const start = new Date(event.starts_at);
  const end = new Date(event.ends_at);
  if (!withEnd || !Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
    return dateTimeLabel(event.starts_at, event.timezone);
  }
  const format = new Intl.DateTimeFormat(undefined, {
    timeZone: event.timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return typeof format.formatRange === "function"
    ? format.formatRange(start, end)
    : `${format.format(start)} – ${format.format(end)}`;
}

type Rsvp = ReturnType<typeof useCommunityGameNights>["rsvp"];

function reminderStatus(result: CommunityGameNightReminderResponse): string {
  if (result.skipped_reason === "all_muted") return "Everyone has reminders turned off.";
  if (result.skipped_reason === "no_recipients") return "No one has signed up to remind yet.";
  return `Reminder sent to ${result.sent_count} ${result.sent_count === 1 ? "member" : "members"}.`;
}

/** The hourly limit is a 429 with its own code; anything else is a generic failure. */
function isReminderRateLimited(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 429 || error.code === "GAME_NIGHT_REMINDER_RATE_LIMITED");
}

const CHAT_AUDIENCE_SUMMARY = {
  members: "all community members",
  attendees: "confirmed attendees",
  attendees_and_waitlisted: "confirmed attendees and the waitlist",
} as const;

/** What publishing does, for the chat access and repeat chosen in the dialog. */
function publishSummary(audience: keyof typeof CHAT_AUDIENCE_SUMMARY, occurrences: number): string {
  const chat = CHAT_AUDIENCE_SUMMARY[audience];
  const what =
    occurrences > 1
      ? `Publishing adds ${occurrences} weekly events to attendee calendars, each with an event chat for ${chat}.`
      : `Publishing adds the event to attendee calendars and opens an event chat for ${chat}.`;
  return `${what} No payment is collected.`;
}
type Remind = ReturnType<typeof useCommunityGameNights>["remind"];

const CHAT_AUDIENCE_LABEL: Record<"members" | "attendees" | "attendees_and_waitlisted", string | null> = {
  members: null,
  attendees: "Attendee chat",
  attendees_and_waitlisted: "Attendee & waitlist chat",
};

/** One action: sign up (or join the waitlist), and undo it. */
function SignUpAction({ event, rsvp, guests }: { event: CommunityGameNightResponse; rsvp: Rsvp; guests: boolean }) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const isOpen = event.status === "published" && new Date(event.ends_at) > new Date();
  const signedUp = event.my_rsvp_status === "going";
  const waitlisted = event.my_rsvp_status === "waitlisted";
  if (!isOpen) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {event.status === "cancelled" ? "Cancelled" : "Ended"}
        {signedUp ? " · You signed up" : ""}
      </Typography>
    );
  }
  const send = (status: "going" | "cant_go", guestCount = event.my_guest_count) =>
    rsvp.mutate({ eventId: event.event_id, data: { status, guest_count: status === "cant_go" ? 0 : guestCount } });
  const failed = rsvp.isError && rsvp.variables?.eventId === event.event_id;
  const failure = failed ? (
    <Box sx={{ flexBasis: "100%" }}>
      <GuardianAwareError error={rsvp.error}>
        <Typography role="alert" variant="body2" color="error.main">
          Your sign-up didn&apos;t change. Try again.
        </Typography>
      </GuardianAwareError>
    </Box>
  ) : null;
  if (signedUp || waitlisted) {
    // Giving up a seat hands it to the waitlist, so confirm only then.
    const releasesSeat = signedUp && event.waitlist_count > 0;
    return (
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography
          role="status"
          variant="body2"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 700, color: waitlisted ? "text.primary" : "primary.main" }}
        >
          {waitlisted ? (
            `Waitlist #${event.my_waitlist_position ?? "—"}`
          ) : (
            <>
              <CheckRounded fontSize="small" aria-hidden />
              Signed up
            </>
          )}
        </Typography>
        {guests && signedUp && event.guest_limit_per_member > 0 ? (
          <Stack direction="row" sx={{ alignItems: "center" }} role="group" aria-label={`Guests for ${event.title}`}>
            <IconButton
              size="small"
              aria-label="Fewer guests"
              disabled={rsvp.isPending || event.my_guest_count === 0}
              onClick={() => send("going", event.my_guest_count - 1)}
            >
              <RemoveRounded fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 56, textAlign: "center" }}>
              +{event.my_guest_count} {event.my_guest_count === 1 ? "guest" : "guests"}
            </Typography>
            {/* A guest needs a free spot; without one the server would move you to the waitlist. */}
            <IconButton
              size="small"
              aria-label="More guests"
              disabled={
                rsvp.isPending ||
                event.my_guest_count >= event.guest_limit_per_member ||
                event.available_spots < 1
              }
              onClick={() => send("going", event.my_guest_count + 1)}
            >
              <AddRounded fontSize="small" />
            </IconButton>
          </Stack>
        ) : null}
        <Button
          size="small"
          color="inherit"
          disabled={rsvp.isPending}
          aria-label={`${waitlisted ? "Leave waitlist" : "Cancel sign-up"} for ${event.title}`}
          onClick={() => (releasesSeat ? setConfirmingCancel(true) : send("cant_go"))}
          sx={{ color: "text.secondary", minWidth: 0 }}
        >
          {waitlisted ? "Leave waitlist" : "Cancel sign-up"}
        </Button>
        {failure}
        <Dialog open={confirmingCancel} onClose={() => setConfirmingCancel(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Give up your spot?</DialogTitle>
          <DialogContent>
            <Typography>
              {event.title} has a waitlist, so your spot goes to the next person. Signing up again puts you at the end of
              the waitlist.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmingCancel(false)}>Keep my spot</Button>
            <Button
              variant="contained"
              onClick={() => {
                setConfirmingCancel(false);
                send("cant_go");
              }}
            >
              Give up spot
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
  const label = event.available_spots > 0 ? "Sign up" : "Join waitlist";
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Button
        size="small"
        variant="contained"
        disabled={rsvp.isPending}
        aria-label={`${label} for ${event.title}`}
        onClick={() => send("going", 0)}
        sx={{ borderRadius: 999, px: 2 }}
      >
        {label}
      </Button>
      {failure}
    </Stack>
  );
}

function EventOptions({
  event,
  disabled,
  onEdit,
  onCancelRequest,
  onRemind,
}: {
  event: CommunityGameNightResponse;
  disabled: boolean;
  onEdit: (event: CommunityGameNightResponse) => void;
  onCancelRequest: (event: CommunityGameNightResponse) => void;
  onRemind?: (event: CommunityGameNightResponse) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = `event-options-${event.event_id}`;
  return (
    <>
      <IconButton
        size="small"
        aria-label={`Event options for ${event.title}`}
        aria-haspopup="menu"
        aria-expanded={anchor ? "true" : undefined}
        aria-controls={anchor ? menuId : undefined}
        disabled={disabled}
        onClick={(click) => setAnchor(click.currentTarget)}
      >
        <MoreHorizRounded fontSize="small" />
      </IconButton>
      <Menu id={menuId} anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); onEdit(event); }}>Edit event</MenuItem>
        {onRemind ? (
          <MenuItem onClick={() => { setAnchor(null); onRemind(event); }}>Send reminder</MenuItem>
        ) : null}
        <MenuItem onClick={() => { setAnchor(null); onCancelRequest(event); }} sx={{ color: "error.main" }}>
          Cancel event
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * One event as a row: date, title, one meta line, then sign-up and event chat.
 * The community page shows the next one; Events lists them all with details.
 */
function EventRow({
  event,
  communityId,
  rsvp,
  remind,
  detailed,
  onEdit,
  onCancelRequest,
}: {
  event: CommunityGameNightResponse;
  communityId: string;
  rsvp: Rsvp;
  remind?: Remind;
  detailed: boolean;
  onEdit?: (event: CommunityGameNightResponse) => void;
  onCancelRequest?: (event: CommunityGameNightResponse) => void;
}) {
  const [reminderResult, setReminderResult] = useState<CommunityGameNightReminderResponse | null>(null);
  const isOpen = event.status === "published" && new Date(event.ends_at) > new Date();
  const chatAllowed = event.can_access_chat !== false;
  // Only an open event can still admit someone to its attendee chat.
  const chatNote = !isOpen
    ? null
    : event.my_rsvp_status === "waitlisted"
      ? "You'll join the chat if a spot opens"
      : "Sign up to join event chat";
  const meta = [
    event.location_label,
    `${event.court_count} ${event.court_count === 1 ? "court" : "courts"}`,
    `${event.going_count}/${event.capacity} going`,
    event.waitlist_count > 0 ? `${event.waitlist_count} waitlisted` : null,
    event.recurrence_frequency === "weekly" ? `Weekly · ${event.recurrence_index}/${event.recurrence_count}` : null,
    detailed ? CHAT_AUDIENCE_LABEL[event.chat_audience ?? "members"] : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const manageable = event.can_manage && event.status !== "cancelled" && onEdit && onCancelRequest;
  return (
    <Box component="article" aria-labelledby={`event-title-${event.event_id}`} sx={{ py: 1.25, px: { xs: 0.5, sm: 0 } }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: "primary.main", fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}
          >
            {whenLabel(event, detailed)}
          </Typography>
          <Typography id={`event-title-${event.event_id}`} component="h3" sx={{ fontWeight: 700, fontSize: "1rem", overflowWrap: "anywhere" }}>
            {event.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {meta}
          </Typography>
          {detailed && (event.description || event.skill_guidance) ? (
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
              {[event.description, event.skill_guidance].filter(Boolean).join(" · ")}
            </Typography>
          ) : null}
        </Box>
        {manageable ? (
          <EventOptions
            event={event}
            disabled={rsvp.isPending || Boolean(remind?.isPending)}
            onEdit={onEdit}
            onCancelRequest={onCancelRequest}
            onRemind={
              remind && isOpen
                ? (target) => remind.mutate(target.event_id, { onSuccess: (res) => setReminderResult(res) })
                : undefined
            }
          />
        ) : null}
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}>
        <SignUpAction event={event} rsvp={rsvp} guests={detailed} />
        {event.chat_conversation_id && !chatAllowed ? (
          chatNote ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              {chatNote}
            </Typography>
          ) : null
        ) : event.chat_conversation_id ? (
          <Button
            size="small"
            component={RouterLink}
            to={`/community/communities/${communityId}/channels/${event.chat_conversation_id}`}
            startIcon={<ForumRounded />}
            aria-label={`Event chat for ${event.title}`}
            sx={{ ml: "auto" }}
          >
            Event chat
          </Button>
        ) : null}
      </Stack>
      {reminderResult ? (
        <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {reminderStatus(reminderResult)}
        </Typography>
      ) : null}
      {remind?.isError && remind.variables === event.event_id ? (
        <Typography role="alert" variant="body2" color="error.main" sx={{ mt: 0.5 }}>
          {isReminderRateLimited(remind.error)
            ? "A reminder already went out in the last hour. Try again later."
            : "Couldn't send the reminder. Try again."}
        </Typography>
      ) : null}
    </Box>
  );
}

function CancelGameNightDialog({
  event,
  onClose,
  cancel,
}: {
  event: CommunityGameNightResponse | null;
  onClose: () => void;
  cancel: ReturnType<typeof useCommunityGameNights>["cancel"];
}) {
  return (
    <Dialog open={Boolean(event)} onClose={cancel.isPending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Cancel this event?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {cancel.isError ? <Alert severity="error">We couldn&apos;t cancel it. Nothing changed.</Alert> : null}
          <Typography>
            {event?.title} will stay in history as cancelled. Confirmed and waitlisted members will be notified.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={cancel.isPending}>Keep event</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!event || cancel.isPending}
          onClick={() => event && cancel.mutate(event.event_id, { onSuccess: onClose })}
        >
          {cancel.isPending ? "Cancelling…" : "Cancel event"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditGameNightDialog({
  event,
  onClose,
  update,
}: {
  event: CommunityGameNightResponse | null;
  onClose: () => void;
  update: ReturnType<typeof useCommunityGameNights>["update"];
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [capacity, setCapacity] = useState("2");
  const [courtCount, setCourtCount] = useState("1");
  const [skillGuidance, setSkillGuidance] = useState("");
  useEffect(() => {
    if (!event) return;
    setTitle(event.title);
    setStartsAt(toLocalInput(new Date(event.starts_at)));
    setEndsAt(toLocalInput(new Date(event.ends_at)));
    setCapacity(String(event.capacity));
    setCourtCount(String(event.court_count));
    setSkillGuidance(event.skill_guidance ?? "");
  }, [event]);
  const valid = title.trim() && startsAt && endsAt && Number(capacity) >= 2 && Number(courtCount) >= 1;
  function submit(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (!event || !valid || update.isPending) return;
    update.mutate({
      eventId: event.event_id,
      data: {
        title: title.trim(),
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        capacity: Number(capacity),
        court_count: Number(courtCount),
        skill_guidance: skillGuidance.trim() || null,
      },
    }, { onSuccess: onClose });
  }
  return (
    <Dialog open={Boolean(event)} onClose={update.isPending ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Edit event</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {update.isError ? <Alert severity="error">We couldn&apos;t save those changes. Your details are still here.</Alert> : null}
            <TextField label="Name" value={title} onChange={(change) => setTitle(change.target.value)} required autoFocus />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Starts" type="datetime-local" value={startsAt} onChange={(change) => setStartsAt(change.target.value)} required fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Ends" type="datetime-local" value={endsAt} onChange={(change) => setEndsAt(change.target.value)} required fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Players" type="number" value={capacity} onChange={(change) => setCapacity(change.target.value)} slotProps={{ htmlInput: { min: 2, max: 500 } }} fullWidth />
              <TextField label="Courts" type="number" value={courtCount} onChange={(change) => setCourtCount(change.target.value)} slotProps={{ htmlInput: { min: 1, max: 100 } }} fullWidth />
            </Stack>
            <TextField label="Skill guidance (optional)" value={skillGuidance} onChange={(change) => setSkillGuidance(change.target.value)} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={update.isPending}>Keep current details</Button>
          <Button type="submit" variant="contained" disabled={!valid || update.isPending}>{update.isPending ? "Saving…" : "Save changes"}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function toLocalInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function CreateGameNightDialog({
  open,
  onClose,
  create,
  ownerFacilityId,
}: {
  open: boolean;
  onClose: () => void;
  create: ReturnType<typeof useCommunityGameNights>["create"];
  ownerFacilityId?: string | null;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const tomorrow = useMemo(() => new Date(Date.now() + 24 * 60 * 60 * 1000), []);
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState(toLocalInput(tomorrow));
  const [endsAt, setEndsAt] = useState(toLocalInput(new Date(tomorrow.getTime() + 2 * 60 * 60 * 1000)));
  const [customLocation, setCustomLocation] = useState("");
  const [useCommunityFacility, setUseCommunityFacility] = useState(Boolean(ownerFacilityId));
  const [capacity, setCapacity] = useState("12");
  const [courtCount, setCourtCount] = useState("2");
  const [guestLimit, setGuestLimit] = useState("0");
  const [skillGuidance, setSkillGuidance] = useState("");
  const [chatAudience, setChatAudience] = useState<"members" | "attendees" | "attendees_and_waitlisted">("members");
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<"none" | "weekly">("none");
  const [recurrenceCount, setRecurrenceCount] = useState("4");

  const valid = title.trim() && startsAt && endsAt &&
    (useCommunityFacility || customLocation.trim()) &&
    Number(capacity) >= 2 && Number(courtCount) >= 1 &&
    (recurrenceFrequency === "none" || (Number(recurrenceCount) >= 2 && Number(recurrenceCount) <= 12));

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || create.isPending) return;
    const data: CommunityGameNightCreate = {
      title: title.trim(),
      description: null,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: new Date(endsAt).toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      facility_id: useCommunityFacility ? (ownerFacilityId ?? null) : null,
      custom_location: useCommunityFacility ? null : customLocation.trim(),
      court_count: Number(courtCount),
      capacity: Number(capacity),
      guest_limit_per_member: Number(guestLimit),
      skill_guidance: skillGuidance.trim() || null,
      notification_policy: "important",
      chat_audience: chatAudience,
      recurrence_frequency: recurrenceFrequency,
      recurrence_count: recurrenceFrequency === "weekly" ? Number(recurrenceCount) : 1,
      publish: true,
    };
    create.mutate(data, {
      onSuccess: () => {
        setTitle("");
        setSkillGuidance("");
        onClose();
      },
    });
  }

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ pr: 7 }}>
          Create an event
          <IconButton aria-label="Close" onClick={onClose} disabled={create.isPending} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            {create.isError ? (
              <GuardianAwareError error={create.error}>
                <Alert severity="error">We couldn&apos;t create that event. Your details are still here.</Alert>
              </GuardianAwareError>
            ) : null}
            <TextField label="Name" value={title} onChange={(event) => setTitle(event.target.value)} required autoFocus />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Starts" type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Ends" type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} required fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Repeat"
                value={recurrenceFrequency}
                onChange={(event) => setRecurrenceFrequency(event.target.value as "none" | "weekly")}
                fullWidth
              >
                <MenuItem value="none">One-time</MenuItem>
                <MenuItem value="weekly">Weekly series</MenuItem>
              </TextField>
              {recurrenceFrequency === "weekly" ? (
                <TextField
                  label="Occurrences (weeks)"
                  type="number"
                  value={recurrenceCount}
                  onChange={(event) => setRecurrenceCount(event.target.value)}
                  slotProps={{ htmlInput: { min: 2, max: 12 } }}
                  fullWidth
                />
              ) : null}
            </Stack>
            {ownerFacilityId ? (
              <FormControlLabel
                control={<Switch checked={useCommunityFacility} onChange={(event) => setUseCommunityFacility(event.target.checked)} />}
                label="Use this community’s facility"
              />
            ) : null}
            {!useCommunityFacility ? (
              <TextField label="Meeting place" value={customLocation} onChange={(event) => setCustomLocation(event.target.value)} required />
            ) : null}
            <Stack direction="row" spacing={2}>
              <TextField label="Players" type="number" value={capacity} onChange={(event) => setCapacity(event.target.value)} slotProps={{ htmlInput: { min: 2, max: 500 } }} fullWidth />
              <TextField label="Courts" type="number" value={courtCount} onChange={(event) => setCourtCount(event.target.value)} slotProps={{ htmlInput: { min: 1, max: 100 } }} fullWidth />
            </Stack>
            <TextField
              select
              label="Event chat access"
              value={chatAudience}
              onChange={(event) => setChatAudience(event.target.value as "members" | "attendees" | "attendees_and_waitlisted")}
              fullWidth
            >
              <MenuItem value="members">All community members</MenuItem>
              <MenuItem value="attendees">Confirmed attendees only</MenuItem>
              <MenuItem value="attendees_and_waitlisted">Confirmed attendees &amp; waitlist</MenuItem>
            </TextField>
            <TextField label="Guests per member" type="number" value={guestLimit} onChange={(event) => setGuestLimit(event.target.value)} slotProps={{ htmlInput: { min: 0, max: 10 } }} helperText="Set 0 so every attendee signs up with their own account." />
            <TextField label="Skill guidance (optional)" value={skillGuidance} onChange={(event) => setSkillGuidance(event.target.value)} placeholder="All levels welcome" multiline minRows={2} />
            <Alert severity="info" icon={<EventRounded />}>
              {publishSummary(chatAudience, recurrenceFrequency === "weekly" ? Number(recurrenceCount) : 1)}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!valid || create.isPending}>
            {create.isPending ? "Publishing…" : "Publish event"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function CommunityGameNights({
  communityId,
  ownerFacilityId,
  compact = false,
}: {
  communityId: string;
  ownerFacilityId?: string | null;
  compact?: boolean;
}) {
  const [includePast, setIncludePast] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CommunityGameNightResponse | null>(null);
  const [cancelling, setCancelling] = useState<CommunityGameNightResponse | null>(null);
  const {
    events,
    polls,
    preferences,
    create,
    update,
    cancel,
    rsvp,
    remind,
    createPoll,
    votePoll,
    convertPoll,
    updatePreferences,
  } = useCommunityGameNights(communityId, includePast, { coordination: !compact });

  if (events.isError) {
    return <Alert severity="warning" role="status" action={<Button onClick={() => void events.refetch()}>Retry</Button>}>Events are unavailable right now.</Alert>;
  }
  if (!events.data) {
    return <Typography role="status" color="text.secondary">Loading events…</Typography>;
  }
  const items = compact ? events.data.items.slice(0, 1) : events.data.items;
  if (compact) {
    return (
      <Stack component="section" spacing={0.5} aria-labelledby="community-next-event">
        <SectionHeader
          id="community-next-event"
          title="Next event"
          action={{ label: "All", to: `/community/communities/${communityId}?view=events`, ariaLabel: "All events" }}
        />
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 } }}>
            {events.data.can_create ? "No upcoming events. Create one from Events." : "No upcoming events."}
          </Typography>
        ) : (
          <EventRow event={items[0]} communityId={communityId} rsvp={rsvp} detailed={false} />
        )}
      </Stack>
    );
  }
  return (
    <Stack component="section" spacing={0.5} aria-labelledby="community-events">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", px: { xs: 0.5, sm: 0 } }}>
        <Typography id="community-events" component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
          {includePast ? "Past events" : "Upcoming events"}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => setIncludePast((value) => !value)}>
            {includePast ? "Upcoming" : "Past"}
          </Button>
          {events.data.can_create ? (
            <Button size="small" startIcon={<AddRounded />} onClick={() => setCreating(true)}>
              New event
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 }, py: 1 }}>
          {includePast
            ? "No past events."
            : events.data.can_create
              ? "No upcoming events. Create one so members can sign up."
              : "No upcoming events. When an organizer schedules one, it shows up here."}
        </Typography>
      ) : (
        <Stack divider={<Divider />}>
          {items.map((event) => (
            <EventRow
              key={event.event_id}
              event={event}
              communityId={communityId}
              rsvp={rsvp}
              remind={remind}
              detailed
              onEdit={setEditing}
              onCancelRequest={setCancelling}
            />
          ))}
        </Stack>
      )}
      <CommunityCoordinationTools
        ownerFacilityId={ownerFacilityId}
        polls={polls}
        createPoll={createPoll}
        votePoll={votePoll}
        convertPoll={convertPoll}
        preferences={preferences}
        updatePreferences={updatePreferences}
      />
      <CreateGameNightDialog open={creating} onClose={() => setCreating(false)} create={create} ownerFacilityId={ownerFacilityId} />
      <EditGameNightDialog event={editing} onClose={() => setEditing(null)} update={update} />
      <CancelGameNightDialog event={cancelling} onClose={() => setCancelling(null)} cancel={cancel} />
    </Stack>
  );
}

